/** İngilizce (varsayılan dil; CLAUDE.md "i18n"). */
export const en = {
  brand: {
    headline: 'Planned maintenance and ISM, on board and ashore.',
    subline:
      'One record from the engine room to the office: works offline at sea, syncs with signed packets when the link is back.',
    features: {
      offline: {
        title: 'Built for sea',
        text: 'Every vessel runs its own server. No internet needed to work.',
      },
      audit: {
        title: 'Audit-ready',
        text: 'Immutable history and approvals designed for BV NR496.',
      },
      sync: {
        title: 'Signed sync',
        text: 'Numbered, hash-chained packets you can verify end to end.',
      },
    },
    footnote: '© {{year}} Fluxesoft · Felixmate {{version}}',
  },
  language: {
    label: 'Language',
    en: 'EN',
    tr: 'TR',
    name: { en: 'English', tr: 'Türkçe' },
  },
  common: { loading: 'Loading' },
  login: {
    title: 'Sign in',
    subtitle: 'Use your company e-mail and password.',
    email: 'E-mail',
    emailPlaceholder: 'name@company.com',
    password: 'Password',
    submit: 'Continue',
    // İki adımlı doğrulama isteğe bağlı (sahip 2026-11-01): kişi Ayarlar ▸ Hesap'tan açar
    footnote: 'Two-step verification available in Settings.',
    // E-postayla sıfırlama yok (e-posta sağlayıcısı yok; ortak 2026-10-30)
    forgot: 'Forgot your password? Ask your company administrator or crew manager to reset it.',
  },
  password: {
    eyebrow: 'First sign-in',
    title: 'Set a new password',
    subtitle: 'Your temporary password must be replaced before you continue.',
    current: 'Temporary password',
    next: 'New password',
    confirm: 'Repeat new password',
    ruleLength: 'At least 12 characters',
    ruleEmail: 'Does not contain your e-mail name',
    ruleMatch: 'Both entries match',
    submit: 'Save password',
    // Açık oturumda kendi parolasını değiştirme (Settings ▸ Account, kullanıcı menüsü)
    changeTitle: 'Change password',
    changeSubtitle:
      'Enter your current password and choose a new one. Your other sessions are signed out.',
    currentPassword: 'Current password',
    cancel: 'Cancel',
    changed: 'Your password has been changed.',
  },
  totpSetup: {
    step: 'Step {{n}} of {{total}}',
    title: 'Set up two-step verification',
    subtitle:
      'Scan the code with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…).',
    qrAlt: 'QR code for your authenticator app',
    manual: 'Can’t scan? Enter this key instead',
    copy: 'Copy key',
    copied: 'Copied',
    code: 'Enter the 6-digit code from the app',
    digit: 'Digit {{n}} of 6',
    submit: 'Verify and finish',
  },
  // Ayarlar ▸ Hesap: iki adımlı doğrulamayı açma ve kapatma (sahip 2026-11-01)
  twoStep: {
    enableTitle: 'Turn on two-step verification',
    disableTitle: 'Turn off two-step verification',
    // Adım göstergesi: sıralı liste; biten adım ekran okuyucuya "completed" diye okunur
    steps: {
      label: 'Steps',
      password: 'Confirm password',
      app: 'Link your app',
      completed: 'completed',
    },
    passwordText: 'Enter your password to continue.',
    password: 'Password',
    continue: 'Continue',
    cancel: 'Cancel',
    scan: 'Scan the code with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…), then enter the 6-digit code it shows.',
    finish: 'Turn on',
    enabled: 'Two-step verification is on.',
    enabledText:
      'From now on, sign-in asks for a code from your authenticator app after your password. Your other sessions have been signed out. If you lose your phone, your company administrator can turn it off.',
    done: 'Done',
    disableText: 'Enter your password and a current code from your authenticator app.',
    disableWarning: 'Sign-in will only ask for your password. Your other sessions are signed out.',
    code: 'Authentication code',
    disable: 'Turn off',
    disabled: 'Two-step verification is off.',
    close: 'Close',
  },
  totp: {
    title: 'Two-step verification',
    subtitle: 'Enter the 6-digit code from your authenticator app.',
    code: 'Authentication code',
    digit: 'Digit {{n}} of 6',
    submit: 'Verify',
    other: 'Sign in with a different account',
  },
  dashboard: {
    subtitle: 'Fleet, crew and compliance at a glance',
    summary: 'Summary',
    tiles: {
      vessels: 'Vessels',
      active: 'Ship servers online',
      onBoard: 'Crew on board',
      ashore: 'Crew ashore',
      certificates: 'Certificate alerts',
      rest: 'Rest violations (yesterday)',
    },
    viewAll: 'View all',
    vessels: {
      title: 'Vessels',
      vessel: 'Vessel',
      imo: 'IMO',
      crew: 'On board',
      server: 'Ship server',
    },
    quickActions: 'Quick Actions',
    unavailable: 'Alerts could not be loaded. They will refresh automatically.',
    // Pano kartlarının hiçbirinin izni yok (ADR 0067 §5)
    noCards: 'No dashboard cards are available for your role.',
    noCardsHint: 'Use the menu to open the pages you can view.',
    actions: {
      addVessel: 'Add vessel',
      certificates: 'Crew certificates',
      restHours: 'Hours of work & rest',
      crew: 'Crew list',
    },
  },
  settings: {
    companyInfo: {
      title: 'Company details',
      legalName: 'Legal name',
      address: 'Address',
      tax: 'Tax office / number',
      phone: 'Phone',
      email: 'General e-mail',
      website: 'Website',
      person: 'Authorised person',
      contract: 'Felixmate subscription',
      status: {
        none: 'Not recorded',
        scheduled: 'Not started',
        active: 'Active',
        expiring: 'Ending soon',
        expired: 'Expired',
      },
      endsOn: 'Ends on {{date}}',
      daysLeft_one: '{{count}} day left',
      daysLeft_other: '{{count}} days left',
      empty: 'No company details recorded yet.',
      readOnly: 'These details are kept by Felixmate. To change them, contact Felixmate support.',
    },
    subtitle: 'Language, account and company settings',
    branding: {
      title: 'Company branding',
      text: 'Shown at the top of the menu in the office and on every vessel. The product name and version line always read Felixmate.',
      name: 'Company name',
      tagline: 'Subtitle',
      vesselSlogan: 'Menu footer slogan',
      vesselSloganHint: 'Two lines separated by “·”. On board, the first line is the vessel name.',
      loading: 'Loading company branding',
      chooseLogo: 'Choose logo',
      removeLogo: 'Remove logo',
      logoHint: 'PNG, JPEG, WebP or SVG, up to 64 KB. Square images look best.',
      logoType: 'Choose a PNG, JPEG, WebP or SVG image.',
      logoSize: 'The logo must be 64 KB or smaller.',
      tooLong: 'Too long.',
      save: 'Save branding',
      saved: 'Saved. Vessels receive it with the next sync.',
    },
    language: { title: 'Language', text: 'English is the default; Turkish is available.' },
    account: {
      title: 'Account',
      name: 'Name',
      email: 'E-mail',
      security: 'Sign-in',
      securityValue: 'Password and two-step verification',
    },
  },
  fleet: {
    title: 'Fleet',
    subtitle: 'Vessels of your company and their ship servers',
    summary: {
      label: 'Fleet summary',
      total: 'Vessels',
      active: 'Ship server active',
      pending: 'Awaiting activation',
    },
    name: 'Vessel',
    imo: 'IMO',
    status: 'Ship server',
    actions: 'Actions',
    activated: 'Active',
    notActivated: 'Not activated',
    // Kurulum aşaması (ADR 0142)
    stage: { label: 'Stage', setup: 'Setup', live: 'Live' },
    goLive: {
      action: 'Go live',
      title: 'Put {{vessel}} into service',
      text: 'During setup the office can also remove document folders it opened by mistake; once the vessel is live, folders are retired instead. The office keeps editing the PMS tree: changes are sent to the vessel and applied at its next sync.',
      warning: 'This cannot be undone from the office. Make sure the document tree is complete.',
      cancel: 'Cancel',
      confirm: 'Go live',
    },
    timeZone: 'Ship time zone: {{zone}}',
    add: 'Add vessel',
    setupCode: 'Setup code',
    more: 'More actions for {{vessel}}',
    closeDialog: 'Close',
    replaceServer: 'Replace ship server…',
    // Yetkisi olmayana düğme yerine (ADR 0151 §1): matristeki yetki ve varsayılanda hangi rolde
    denied: {
      permission: '{{module}} ▸ {{action}}',
      // Kapsam (ADR 0151 Ek): gemi kapsamlı hücre filo işine yetmez
      scope: { fleet: 'for the whole fleet', vessel: 'for this vessel' },
      // Menüde düğmenin adı zaten yazılı: yalnız yetki
      short: 'Needs {{permission}} {{scope}} ({{role}} by default).',
      add: 'Adding vessels needs {{permission}} {{scope}} ({{role}} by default).',
      setupCode: 'Setup codes need {{permission}} {{scope}} ({{role}} by default).',
      replaceServer:
        'Replacing the ship server needs {{permission}} {{scope}} ({{role}} by default).',
      goLive: 'Going live needs {{permission}} {{scope}} ({{role}} by default).',
      particulars:
        'Editing vessel particulars needs {{permission}} {{scope}} ({{role}} by default).',
    },
    emptyTitle: 'No vessels yet',
    emptyText: 'Add your first vessel, then activate its ship server with a setup code.',
    error: 'The fleet could not be loaded.',
    retry: 'Try again',
    guide: {
      title: 'Bringing a vessel online',
      step1: 'Add the vessel with its IMO number and ship time zone.',
      step2: 'Create a setup code. It is valid for 24 hours and can be used once.',
      step3: 'Install the ship server on the master’s PC and enter the code.',
      step4: 'Records then reach the office in signed packets whenever the ship is online.',
    },
    addForm: {
      title: 'Add vessel',
      description:
        'The office keeps the vessel’s data until its ship server is activated; from then on the vessel writes its own records.',
      name: 'Vessel name',
      namePlaceholder: 'M/V Ocean Star',
      imo: 'IMO number',
      imoHint: '7 digits',
      imoInvalid: 'Enter the 7-digit IMO number.',
      nameRequired: 'Enter the vessel name.',
      timeZone: 'Ship time zone',
      timeZoneHint: 'Used for the “day the work was done” on board.',
      template: 'Document template',
      templateHint:
        'The ISM Forms and Manuals folder tree is copied from this template when the vessel is added. Only Felixmate can change it later.',
      templateSingle:
        'Your company has one document template. The folder tree is copied from it when the vessel is added.',
      templateLoading: 'Loading templates…',
      templateRequired: 'Choose a document template.',
      templateErrors: {
        GENERIC: 'The document templates could not be loaded. Close the dialog and try again.',
        NETWORK: 'The document templates could not be loaded (no connection). Try again.',
        DOC_TEMPLATE_REQUIRED:
          'No document template is enabled for your company. Ask Felixmate support to enable one.',
      },
      cancel: 'Cancel',
      submit: 'Add vessel',
    },
    code: {
      title: 'Setup code for {{vessel}}',
      intro:
        'The ship server uses this code once to register with the office. Anyone with the code can activate a server for this vessel, so share it only with the master.',
      replaceWarning:
        'This vessel already has an active ship server. Activating a new one retires the current server; packets it sends afterwards are rejected and reported.',
      create: 'Create setup code',
      // Kritik işlem: işlemi yapanın parolası (ADR 0075 §5, ADR 0151 §2)
      password: 'Your password',
      passwordHelp:
        'A setup code connects a new ship server to this vessel. Enter your own password to confirm.',
      creating: 'Creating…',
      label: 'Setup code',
      copy: 'Copy code',
      copied: 'Copied',
      validUntil: 'Valid until {{date}}',
      onceOnly: 'This code is shown only once. Create a new one if it is lost.',
      next: 'On the master’s PC, enter the code when the ship server setup asks for it.',
      close: 'Done',
      cancel: 'Cancel',
    },
  },
  errors: {
    SHIP_SERVER_NOT_ACTIVATED:
      'Activate the vessel’s ship server first; until then the office keeps the vessel’s data.',
    ALREADY_LIVE: 'The vessel is already live.',
    DOC_TEMPLATE_REQUIRED:
      'No document template is enabled for your company. Ask Felixmate support to enable one.',
    DOC_TEMPLATE_UNAVAILABLE: 'This document template is not enabled for your company.',
    INVALID_LOGO: 'The logo must be a PNG, JPEG, WebP or SVG image.',
    LOGO_TOO_LARGE: 'The logo must be 64 KB or smaller.',
    INVALID_CREDENTIALS: 'E-mail or password is incorrect.',
    INVALID_TOTP: 'The code is not valid. Check the time on your phone and try again.',
    TOTP_ALREADY_ENABLED: 'Two-step verification is already on for your account.',
    TOTP_UNAVAILABLE: 'Two-step verification is not available for this account.',
    TOTP_NOT_ENROLLED: 'Two-step verification is already off for your account.',
    // Açık oturumda parola onayı (ofisin ortak yeniden doğrulama sayacı; 15 dakikada 5 hata)
    INVALID_PASSWORD: 'The password is not correct.',
    // Parola değiştirme sayfası (sunucu kodu INVALID_CREDENTIALS)
    INVALID_CURRENT_PASSWORD: 'The current password is not correct.',
    TOO_MANY_ATTEMPTS: 'Too many attempts. Wait 15 minutes and try again.',
    PASSWORD_TOO_SHORT: 'The password must be at least 12 characters.',
    PASSWORD_TOO_LONG: 'The password is too long.',
    PASSWORD_CONTAINS_EMAIL: 'The password must not contain your e-mail name.',
    NETWORK: 'No connection to the server. Check your network and try again.',
    UNAUTHENTICATED: 'Your session has ended. Please sign in again.',
    VALIDATION_FAILED: 'Some fields are not valid. Check them and try again.',
    FORBIDDEN: 'You do not have permission for this.',
    EXPIRES_BEFORE_ISSUED: 'The expiry date must be after the issue date.',
    ROW_VERSION_CONFLICT: 'Someone else changed this record. Reload and try again.',
    REAUTHENTICATION_REQUIRED: 'Enter your password.',
    GENERIC: 'Something went wrong. Please try again.',
  },
} as const;

/** Bütün dillerin uyması gereken biçim: anahtarlar aynı, metinler serbest. */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };
export type Messages = Shape<typeof en>;
